'use client';

import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  IconExternalLink,
  IconEye,
  IconCopy,
  IconQuote,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import { ProjectLinkDialog } from '@/components/projects/ProjectLinkDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { ProjectRecord, ProjectDocument } from '@/lib/projects/project-types';
import type { NavigationContext } from '@/lib/navigation-types';
import { formatDateNL } from '@/lib/projects/project-utils';
import { buildCitation } from '@/lib/projects/citation';
import { safeHref } from '@/lib/safe-href';
import { useSheetKeyboardNav } from '@/hooks/useSheetKeyboardNav';
import { DetailSection } from '@/components/projects/detail/DetailSection';
import { DetailMetaRow } from '@/components/projects/detail/DetailMetaRow';
import { ProjectDetailSheetSkeleton } from '@/components/projects/states/ProjectDetailSheetSkeleton';
import { ProjectDocumentsSection } from '@/components/projects/documents/ProjectDocumentsSection';
import { DocumentPreviewDialog } from '@/components/projects/documents/DocumentPreviewDialog';

interface ProjectDetailSheetProps {
  project: ProjectRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context?: NavigationContext;
  isLoading?: boolean;
}

export function ProjectDetailSheet({
  project,
  open,
  onOpenChange,
  context,
  isLoading,
}: ProjectDetailSheetProps) {
  const canGoPrev = context && context.currentIndex > 0;
  const canGoNext = context && context.currentIndex >= 0 && context.currentIndex < context.orderedIds.length - 1;

  const handlePrev = useCallback(() => {
    if (canGoPrev && context) context.onNavigate(context.currentIndex - 1);
  }, [canGoPrev, context]);

  const handleNext = useCallback(() => {
    if (canGoNext && context) context.onNavigate(context.currentIndex + 1);
  }, [canGoNext, context]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<ProjectDocument | null>(null);

  const handlePreviewDoc = useCallback((doc: ProjectDocument) => {
    setSelectedDoc(doc);
    setPreviewOpen(true);
  }, []);

  useSheetKeyboardNav(open, handlePrev, handleNext);

  const handleCopySummary = async () => {
    if (!project) return;
    try {
      await navigator.clipboard.writeText(project.executiveSummary);
      toast.success('Summary copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleCopyCitation = async () => {
    if (!project) return;
    try {
      await navigator.clipboard.writeText(buildCitation(project));
      toast.success('Citation copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto w-full sm:w-[420px] md:w-[600px] sm:max-w-[600px]! p-0">
        {open && isLoading && !project && <ProjectDetailSheetSkeleton />}
        {project && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <SheetHeader className="px-6 pt-5 pb-0 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <SheetTitle className="text-lg font-semibold line-clamp-2 leading-tight">
                    <TooltipProvider delayDuration={300}>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {context && context.currentIndex >= 0 && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 cursor-pointer"
                                  disabled={!canGoPrev}
                                  onClick={handlePrev}
                                  aria-label="Previous project"
                                >
                                  <IconChevronLeft className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Previous project (↑)</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 cursor-pointer"
                                  disabled={!canGoNext}
                                  onClick={handleNext}
                                  aria-label="Next project"
                                >
                                  <IconChevronRight className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Next project (↓)</TooltipContent>
                            </Tooltip>
                          </>
                        )}

                      </div>
                    </TooltipProvider>
                    {project.projectName}
                  </SheetTitle>
                  <SheetDescription className="text-sm text-muted-foreground mt-1">
                    {project.organizationName}
                  </SheetDescription>
                </div>

              </div>

              {/* Badge row */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{project.country.join(', ')}</Badge>
                <Badge variant="outline">{project.sector}</Badge>
                <Badge variant="secondary">{project.sourceDomain}</Badge>
                {project.callForProposalLink && (
                  <Badge variant="default">CFP</Badge>
                )}
              </div>
            </SheetHeader>

            {/* Primary Actions */}
            <div className="px-6 pt-4 pb-1">
              <div className="flex flex-wrap gap-2">
                {project.callForProposalLink ? (
                  <>
                    <Button size="sm" asChild className="flex-1 sm:flex-none">
                      <a
                        href={safeHref(project.callForProposalLink!)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconExternalLink className="size-4 mr-1.5" />
                        Call for proposal
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={safeHref(project.projectLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open project page in new tab"
                      >
                        <IconExternalLink className="size-4 mr-1.5" />
                        Project page
                      </a>
                    </Button>
                    <ProjectLinkDialog
                      url={project.projectLink}
                      title={project.projectName}
                      trigger={
                        <Button variant="ghost" size="icon" className="size-8" aria-label="Preview project page in app">
                          <IconEye className="size-4" />
                        </Button>
                      }
                    />
                  </>
                ) : (
                  <>
                    <Button size="sm" asChild className="flex-1 sm:flex-none">
                      <a
                        href={safeHref(project.projectLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open project page in new tab"
                      >
                        <IconExternalLink className="size-4 mr-1.5" />
                        Project page
                      </a>
                    </Button>
                    <ProjectLinkDialog
                      url={project.projectLink}
                      title={project.projectName}
                      trigger={
                        <Button variant="ghost" size="icon" className="size-8" aria-label="Preview project page in app">
                          <IconEye className="size-4" />
                        </Button>
                      }
                    />
                  </>
                )}
                <Button variant="link" size="sm" asChild className="text-muted-foreground">
                  <a
                    href={`https://${project.sourceDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconExternalLink className="size-3.5 mr-1" />
                    Source link
                  </a>
                </Button>
              </div>
            </div>

            <Separator className="my-4 mx-6 w-auto" />

            {/* Content Sections */}
            <div className="px-6 pb-6 sm:pb-12 space-y-6 flex-1">
              {/* Executive Summary */}
              <DetailSection title="Executive Summary">
                <p className="text-sm leading-6 text-muted-foreground">
                  {project.executiveSummary}
                </p>
              </DetailSection>
              {/* Utilities */}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleCopySummary} className="cursor-pointer">
                  <IconCopy className="size-3.5 mr-1.5" />
                  Copy summary
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyCitation} className="cursor-pointer">
                  <IconQuote className="size-3.5 mr-1.5" />
                  Copy citation
                </Button>
              </div>

              <Separator />

              {/* Timeline */}
              <DetailSection title="Timeline">
                <div className="space-y-1.5">
                  <DetailMetaRow label="Start date" value={formatDateNL(project.startDate)} />
                  <DetailMetaRow label="End date" value={formatDateNL(project.endDate)} />
                </div>
              </DetailSection>

              <Separator />

              {/* Organisation Details */}
              {(project.organizationType || project.inCooperation || project.euCountry) && (
                <>
                  <DetailSection title="Organisation Details">
                    <div className="space-y-1.5">
                      {project.organizationType && (
                        <DetailMetaRow label="Type of organisation" value={project.organizationType} />
                      )}
                      {project.inCooperation && (
                        <DetailMetaRow label="In cooperation with" value={project.inCooperation} />
                      )}
                      {project.euCountry && (
                        <DetailMetaRow label="EU country" value={project.euCountry} />
                      )}
                    </div>
                  </DetailSection>
                  <Separator />
                </>
              )}

              {/* Documents */}
              <ProjectDocumentsSection
                documents={project.documents}
                projectTitle={project.projectName}
                onPreview={handlePreviewDoc}
              />

              <Separator />

              

              {/* Provenance (collapsed) */}
              <Accordion type="single" collapsible className="border-none rounded-none">
                <AccordionItem value="provenance" className="border-none">
                  <AccordionTrigger className="px-0 py-2 text-sm font-semibold tracking-tight hover:no-underline">
                    Provenance
                  </AccordionTrigger>
                  <AccordionContent className="px-0">
                    <div className="space-y-1.5">
                      <DetailMetaRow label="Source domain" value={project.sourceDomain} />
                      <DetailMetaRow label="Scraped URL" value={project.projectLink} />
                      <DetailMetaRow label="Updated at" value={formatDateNL(project.updatedAt)} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Bottom spacing */}
            <div className="h-6" />
          </div>
        )}
      </SheetContent>

      {/* Document preview dialog — rendered outside SheetContent so Esc closes dialog first */}
      <DocumentPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        doc={selectedDoc}
        projectTitle={project?.projectName}
      />
    </Sheet>
  );
}
